import { env, isProduction, type AppEnv } from '../../lib/config/env';

/**
 * Permanent, non-dismissible environment banner.
 *
 * - Always visible in Development, Staging/UAT, and Preview.
 * - Clearly distinguishable per environment (colour + label).
 * - Renders NOTHING in Production.
 * - Not removable by any ordinary user preference (no close control, no
 *   localStorage/cookie dismissal) — it is a safety indicator, not a
 *   notification.
 */

const STYLES: Record<Exclude<AppEnv, 'production'>, { label: string; bg: string; fg: string }> = {
  development: { label: 'DEVELOPMENT — synthetic data, not production', bg: '#b45309', fg: '#fff7ed' },
  staging: { label: 'STAGING / UAT — test environment, not production', bg: '#6d28d9', fg: '#f5f3ff' },
  preview: { label: 'PREVIEW BUILD — not production', bg: '#334155', fg: '#e2e8f0' },
};

export default function EnvironmentBanner() {
  if (isProduction) return null;

  const style = STYLES[env.appEnv as Exclude<AppEnv, 'production'>];
  // Defensive: if appEnv is somehow unmapped, still show a generic non-prod strip.
  const { label, bg, fg } = style ?? {
    label: `NON-PRODUCTION (${env.appEnv})`,
    bg: '#334155',
    fg: '#e2e8f0',
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="environment-banner"
      data-app-env={env.appEnv}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        width: '100%',
        backgroundColor: bg,
        color: fg,
        textAlign: 'center',
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '0.04em',
        padding: '2px 8px',
        lineHeight: 1.3,
        opacity: 0.9,
        borderBottom: `1px solid rgba(0,0,0,0.2)`,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
  );
}
